import { ReactElement, useMemo } from 'react'
import compact from 'lodash-es/compact'
import keyBy from 'lodash-es/keyBy'
import reject from 'lodash-es/reject'
import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import type { ShearConnectionDTO } from '@api/fastAPI.schemas'
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd'
import { Box } from '@ui/structure'
import { swapElements } from '@utils'
import AddShearConnection from './components/AddShearConnection'
import ListItem from './components/ListItem'

interface Props {
  title: string
  /** Ordered shear connection guids; the first one that carries the flow is used. */
  value: string[]
  onChange: (next: string[]) => void
  availableConnections: ShearConnectionDTO[]
  'data-cy'?: string
}

const ShearConnectionPrioritizationList = ({
  title,
  value,
  onChange,
  availableConnections,
  'data-cy': dataCy,
}: Props): ReactElement => {
  const connectionByGuid = useMemo(
    () => keyBy(availableConnections, 'guid'),
    [availableConnections],
  )

  // A guid without a library entry is dropped here rather than synced away: the
  // backend already prunes deleted connectors from the prioritization, so the
  // stale guid only ever shows up between a delete and its refetch.
  const assignedConnections = useMemo(
    () => compact(value.map(guid => connectionByGuid[guid])),
    [value, connectionByGuid],
  )

  // Drag/delete indices refer to the rendered rows, so changes must be applied
  // to the rendered guids — indexing into `value` would hit the wrong entry
  // while it still contains a stale guid.
  const assignedGuids = useMemo(
    () => assignedConnections.map(connection => connection.guid),
    [assignedConnections],
  )

  // only offer connectors that have not been assigned to this category yet
  const selectableConnections = useMemo(
    () => reject(availableConnections, connection => value.includes(connection.guid)),
    [availableConnections, value],
  )

  const onDragEnd = (result: DropResult) => {
    // dropped outside the list
    if (!result.destination) return
    if (result.destination.index === result.source.index) return

    onChange(swapElements(assignedGuids, result.source.index, result.destination.index))
  }

  return (
    <Stack direction="column">
      <Box>
        <Box display="flex" mb={1} justifyContent="space-between">
          <Typography variant="h6" mb={1}>
            {title} {`(${assignedConnections.length})`}
          </Typography>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell>
                <Box pl={4}>Prio</Box>
              </TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Kurzbezeichnung</TableCell>
              <TableCell>Kommentar</TableCell>
              <TableCell>Abstand (m)</TableCell>
              <TableCell>
                <>
                  R<sub>d</sub> Erdbeben (kN)
                </>
              </TableCell>
              <TableCell>
                <>
                  R<sub>d</sub> Wind (kN)
                </>
              </TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId={`shear-prioritization-${dataCy ?? title}`}>
              {provided => (
                <TableBody
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  sx={{ width: '100%' }}
                >
                  {assignedConnections.map((connection, index) => (
                    <Draggable
                      key={connection.guid + index}
                      draggableId={connection.guid + index}
                      index={index}
                    >
                      {(draggableProvided, snapshot) => (
                        <ListItem
                          prio={index + 1}
                          connection={connection}
                          provided={draggableProvided}
                          isDragging={snapshot.isDragging}
                          onDelete={() => onChange(reject(assignedGuids, (guid, i) => i === index))}
                        />
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </DragDropContext>
          <TableFooter>
            <AddShearConnection
              options={selectableConnections}
              onAdd={connection => onChange([...value, connection.guid])}
              data-cy={`${dataCy}-add-shear-connection`}
            />
          </TableFooter>
        </Table>
      </TableContainer>
    </Stack>
  )
}

export default ShearConnectionPrioritizationList
